import { AlertOctagon } from 'lucide-react';
import { pauseReasonOf, explainPause, isAutoPaused } from '../lib/campaignPause.js';
import { formatDateTimeWithZone } from '../lib/format.js';

// "The platform stopped your campaign, and here is why."
//
// The health monitor auto-pauses a campaign on systemic AI malfunction. That is a
// state change the user did not make and got no signal about — before this, the
// only visible effect was a campaign that quietly stopped dialling while the page
// still said it was running.
//
// Written to be impossible to mistake for a user action, and to lead with the
// consequence ("stopped dialling") rather than the mechanism. The monitor's own
// reason string is shown verbatim underneath as the evidence, because a rate and
// a sample size are what someone needs to judge whether it was right.

export default function AutoPauseBanner({ campaign, className = '' }) {
  // isAutoPaused correlates `pausedAt` with the marker's own timestamp. The marker
  // is never cleared, so its mere presence only means "was auto-paused once" — a
  // later pause the USER performed must not be reported as a platform fault.
  if (!isAutoPaused(campaign)) return null;
  const marker = pauseReasonOf(campaign);
  if (!marker) return null;

  const plain = explainPause(marker.reason);

  return (
    <div
      className={`rounded-lg border border-rose-500/40 bg-rose-950/30 p-4 ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertOctagon size={18} className="mt-0.5 shrink-0 text-rose-300" />
        <div className="min-w-0">
          <h3 className="font-semibold text-rose-200">
            Alfred paused this campaign automatically — it has stopped dialling.
          </h3>
          <p className="mt-1 text-sm text-stone-300">
            {plain
              ? `${plain} `
              : 'The health monitor detected a systemic problem with the calls. '}
            Nobody on your team did this. It stays paused until you start it again.
          </p>

          <div className="mt-3 rounded-md border border-stone-800 bg-stone-950/50 px-3 py-2">
            <div className="text-xs uppercase tracking-wide text-stone-500">What tripped it</div>
            {/* The monitor's string verbatim — the rates and sample sizes are the
                evidence for judging whether the pause was warranted. */}
            <div className="mt-0.5 font-mono text-xs text-stone-300 break-words">
              {marker.reason}
            </div>
            {marker.at && (
              <div className="mt-1 text-xs text-stone-500">
                {formatDateTimeWithZone(marker.at.toISOString())}
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-stone-400">
            Listen to a few recent calls before restarting. If the campaign is restarted while the
            problem is still there, the monitor will pause it again.
          </p>
        </div>
      </div>
    </div>
  );
}

// Compact form for list rows, where a full banner would crowd every other campaign.
export function AutoPauseChip({ campaign }) {
  if (!isAutoPaused(campaign)) return null;
  return (
    <span
      className="badge gap-1 bg-rose-950/60 text-rose-300"
      title="Paused automatically by the health monitor — open the campaign for the reason."
    >
      <AlertOctagon size={12} />
      auto-paused
    </span>
  );
}
